import React, { useState, useEffect, useRef } from 'react';
import { Check, ClipboardList, Pause, Pencil, Play } from 'lucide-react';

interface ProposedPlanProps {
    plan: {
        research_focus: string;
        deliverable_type: string;
        key_areas: string[];
        structure_suggestion: string;
    };
    isAccepted?: boolean;
    autoAccept?: boolean;
    isLoading?: boolean;
    onAccept?: () => void;
    onReject?: () => void;
}

const AUTO_ACCEPT_SECONDS = 20;

const ProposedPlan: React.FC<ProposedPlanProps> = ({ plan, isAccepted, autoAccept, isLoading, onAccept, onReject }) => {
    const [countdown, setCountdown] = useState(AUTO_ACCEPT_SECONDS);
    const [isPaused, setIsPaused] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const hasAutoAcceptedRef = useRef(false);

    // Auto-accept countdown timer
    useEffect(() => {
        // Only run timer if auto-accept is enabled, not already accepted, and not loading
        if (!autoAccept || isAccepted || isLoading || isPaused || hasAutoAcceptedRef.current) {
            return;
        }

        // Start countdown using setInterval for display updates
        timerRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    // Time's up - clear interval first
                    if (timerRef.current) {
                        clearInterval(timerRef.current);
                        timerRef.current = null;
                    }
                    // Use setTimeout to call onAccept outside of state update
                    // and use ref to prevent duplicate calls
                    if (!hasAutoAcceptedRef.current) {
                        hasAutoAcceptedRef.current = true;
                        setTimeout(() => onAccept?.(), 0);
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [autoAccept, isAccepted, isLoading, isPaused, onAccept]);

    // Reset countdown and hasAutoAccepted when autoAccept changes or component receives new plan
    useEffect(() => {
        if (autoAccept && !isAccepted) {
            setCountdown(AUTO_ACCEPT_SECONDS);
            setIsPaused(false);
            hasAutoAcceptedRef.current = false;
        }
    }, [autoAccept, isAccepted]);

    const handlePauseToggle = () => {
        setIsPaused(!isPaused);
    };

    return (
        <div className="border border-border-default bg-bg-warm rounded-xl p-3 mt-2 mb-2">
            <div className="flex items-center mb-2">
                <div className="w-5 h-5 bg-bg-warm rounded-full flex items-center justify-center mr-2">
                    <ClipboardList size={16} strokeWidth={1.5} className="text-fg-secondary" aria-hidden="true" />
                </div>
                <h3 className="text-fg-secondary font-medium chat-message-text">Proposed research plan</h3>
            </div>
            
            <div className="space-y-2">
                <div className="bg-surface rounded-lg p-2 border border-divider">
                    <h4 className="chat-message-text font-medium text-fg-secondary mb-1">Research focus</h4>
                    <p className="text-fg-secondary chat-message-text leading-relaxed">{plan.research_focus}</p>
                </div>
                
                <div className="bg-surface rounded-lg p-2 border border-divider">
                    <h4 className="chat-message-text font-medium text-fg-secondary mb-1">Deliverable</h4>
                    <p className="text-fg-secondary chat-message-text leading-relaxed">{plan.deliverable_type}</p>
                </div>
                
                {plan.key_areas && plan.key_areas.length > 0 && (
                    <div className="bg-surface rounded-lg p-2 border border-divider">
                        <h4 className="chat-message-text font-medium text-fg-secondary mb-1">Key areas to cover</h4>
                        <ul className="space-y-1">
                            {plan.key_areas.map((area, index) => (
                                <li key={index} className="text-fg-secondary chat-message-text leading-relaxed flex items-start">
                                    <span className="text-accent font-medium mr-2 mt-0.5 flex-shrink-0 chat-message-text">{index + 1}.</span>
                                    <span>{area}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                
                {plan.structure_suggestion && (
                    <div className="bg-surface rounded-lg p-2 border border-divider">
                        <h4 className="chat-message-text font-medium text-fg-secondary mb-1">Suggested structure</h4>
                        <p className="text-fg-secondary chat-message-text leading-relaxed">{plan.structure_suggestion}</p>
                    </div>
                )}
            </div>
            
            <div className="mt-3 pt-2 border-t border-border-default">
                {isAccepted ? (
                    /* Accepted State - Show confirmation */
                    <div className="flex items-center justify-center gap-2 text-success">
                        <Check size={16} strokeWidth={1.5} aria-hidden="true" />
                        <span className="text-sm font-medium">Plan accepted</span>
                    </div>
                ) : (
                    /* Active State - Show action buttons */
                    <>
                        <p className="text-fg-secondary chat-message-text text-center leading-relaxed mb-3">
                            Would you like me to proceed with this research plan?
                        </p>

                        {/* Action Buttons */}
                        <div className="flex items-center justify-center gap-2">
                            <button
                                onClick={onAccept}
                                className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors duration-200 text-sm font-medium"
                            >
                                <Check size={16} strokeWidth={1.5} aria-hidden="true" />
                                Accept plan
                            </button>
                            <button
                                onClick={onReject}
                                className="flex items-center gap-2 px-4 py-2 bg-bg-warm text-fg-secondary rounded-lg hover:bg-border-default transition-colors duration-200 text-sm font-medium"
                            >
                                <Pencil size={16} strokeWidth={1.5} aria-hidden="true" />
                                Modify plan
                            </button>
                        </div>

                        {/* Auto-accept countdown */}
                        {autoAccept && !isLoading && (
                            <div className="flex items-center justify-center gap-2 mt-3">
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-accent-tint border border-accent-border rounded-lg">
                                    <span className="text-accent-hover text-xs font-medium">
                                        Auto-accepting in {countdown}s
                                    </span>
                                    <button
                                        onClick={handlePauseToggle}
                                        className="text-accent hover:text-accent-hover transition-colors"
                                        title={isPaused ? "Resume timer" : "Pause timer"}
                                    >
                                        {isPaused ? (
                                            <Play size={20} strokeWidth={1.5} aria-hidden="true" />
                                        ) : (
                                            <Pause size={20} strokeWidth={1.5} aria-hidden="true" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        <p className="text-fg-tertiary chat-message-text text-center text-xs mt-2">
                            Or type your adjustments in the chat box below
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default ProposedPlan;
